/**
 * Canvas glyph drawing for the map renderer (WI #5531).
 *
 * The SVG scene draws icons with <UiIcon>, which renders Lucide components. The
 * canvas engine can't mount components, so we redraw the handful of glyphs it
 * needs (activity state icons, the favourite star, the special-assignment alert)
 * directly with canvas primitives, using Lucide's exact node data (extracted
 * 1:1 from `lucide-vue-next` v0.555 — keep in sync if the icon set is bumped).
 *
 * Each icon is authored in Lucide's 24×24 space with stroke-width 2 and round
 * caps/joins; `drawIcon` scales it to the requested size. The stroke scales WITH
 * the icon (2 units in the 24-space → `2 * size/24` px on screen), matching how
 * the SVG renders these: `UiIcon`'s `absolute-stroke-width` only keeps the stroke
 * at 2px relative to the *declared* 24px size, but the map wrappers (UiStateIcon
 * etc.) display the icon scaled down (e.g. a 15px label glyph), which scales the
 * stroke down too — so a constant 2px would be visibly too thick.
 */
import { cssVar } from './palette';
import { SVG_PATH_DATA } from './shapes';
import type { ItemState, ItemStatus } from '@/common';

type Primitive =
  | { kind: 'path'; d: string }
  | { kind: 'circle'; cx: number; cy: number; r: number }
  | { kind: 'line'; x1: number; y1: number; x2: number; y2: number }
  | { kind: 'rect'; x: number; y: number; w: number; h: number; r: number };

const p = (d: string): Primitive => ({ kind: 'path', d });
const c = (cx: number, cy: number, r: number): Primitive => ({
  kind: 'circle',
  cx,
  cy,
  r,
});
const l = (x1: number, y1: number, x2: number, y2: number): Primitive => ({
  kind: 'line',
  x1,
  y1,
  x2,
  y2,
});
const r = (
  x: number,
  y: number,
  w: number,
  h: number,
  rad: number,
): Primitive => ({
  kind: 'rect',
  x,
  y,
  w,
  h,
  r: rad,
});

/** Lucide node data (24×24) for the glyphs the canvas scene draws. */
export const LUCIDE: Record<string, Primitive[]> = {
  Ban: [p('M4.929 4.929 19.07 19.071'), c(12, 12, 10)],
  Map: [
    p(
      'M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z',
    ),
    p('M15 5.764v15'),
    p('M9 3.236v15'),
  ],
  Target: [c(12, 12, 10), c(12, 12, 6), c(12, 12, 2)],
  PlusSquare: [r(3, 3, 18, 18, 2), p('M8 12h8'), p('M12 8v8')],
  MinusSquare: [r(3, 3, 18, 18, 2), p('M8 12h8')],
  Triangle: [
    p('M13.73 4a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z'),
  ],
  CheckCircle: [p('M21.801 10A10 10 0 1 1 17 3.335'), p('m9 11 3 3L22 4')],
  XCircle: [c(12, 12, 10), p('m15 9-6 6'), p('m9 9 6 6')],
  AlertCircle: [c(12, 12, 10), l(12, 8, 12, 12), l(12, 16, 12.01, 16)],
  Info: [c(12, 12, 10), p('M12 16v-4'), p('M12 8h.01')],
  Star: [
    p(
      'M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z',
    ),
  ],
  Eye: [
    p(
      'M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0',
    ),
    c(12, 12, 3),
  ],
  EyeOff: [
    p(
      'M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49',
    ),
    p('M14.084 14.158a3 3 0 0 1-4.242-4.242'),
    p(
      'M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143',
    ),
    p('m2 2 20 20'),
  ],
  // WI #5563/#5599: workstream expand (outward diagonal arrows) / collapse
  // (inward diagonal arrows), drawn next to the hide-eye in the sidebar row.
  Maximize2: [p('M15 3h6v6'), p('m21 3-7 7'), p('m3 21 7-7'), p('M9 21H3v-6')],
  Minimize2: [
    p('m14 10 7-7'),
    p('M20 10h-6V4'),
    p('m3 21 7-7'),
    p('M4 14h6v6'),
  ],
  Trash2: [
    p('M3 6h18'),
    p('M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6'),
    p('M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2'),
    l(10, 11, 10, 17),
    l(14, 11, 14, 17),
  ],
  // WI #5622: compress-tool fix toggle — closed padlock = fixed, open = fixable.
  Lock: [r(3, 11, 18, 11, 2), p('M7 11V7a5 5 0 0 1 10 0v4')],
  LockOpen: [r(3, 11, 18, 11, 2), p('M7 11V7a5 5 0 0 1 9.9-1')],
  Clock: [c(12, 12, 10), p('M12 6v6l4 2')],
};

export type CanvasIconName = keyof typeof LUCIDE;

const pathCache = new Map<string, Path2D>();

function path2d(d: string): Path2D {
  let cached = pathCache.get(d);
  if (!cached) {
    cached = new Path2D(d);
    // Tag with the source `d` so the vector PDF export can replay it — the
    // `svgcanvas` ctx can't consume a `Path2D` (see draw/shapes.ts / export).
    (cached as unknown as Record<symbol, string>)[SVG_PATH_DATA] = d;
    pathCache.set(d, cached);
  }

  return cached;
}

/**
 * Draw a Lucide glyph centred at (x, y) at the given pixel size in `color`.
 * `fill` fills the (single-path) glyph as well as stroking it — used for the
 * solid favourite star.
 */
export function drawIcon(
  ctx: CanvasRenderingContext2D,
  name: CanvasIconName,
  x: number,
  y: number,
  size: number,
  color: string,
  fill = false,
): void {
  const prims = LUCIDE[name];
  if (!prims) {
    return;
  }
  const scale = size / 24;
  ctx.save();
  ctx.translate(x - size / 2, y - size / 2);
  ctx.scale(scale, scale);
  // Stroke-width 2 in the 24-space scales with the icon → 2 * size/24 px on
  // screen, matching the SVG's scaled-down Lucide glyphs (see file header).
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = color;
  ctx.fillStyle = color;

  for (const prim of prims) {
    if (prim.kind === 'path') {
      const path = path2d(prim.d);
      if (fill) {
        ctx.fill(path);
      }
      ctx.stroke(path);
    } else if (prim.kind === 'circle') {
      ctx.beginPath();
      ctx.arc(prim.cx, prim.cy, prim.r, 0, Math.PI * 2);
      ctx.stroke();
    } else if (prim.kind === 'line') {
      ctx.beginPath();
      ctx.moveTo(prim.x1, prim.y1);
      ctx.lineTo(prim.x2, prim.y2);
      ctx.stroke();
    } else {
      const rad = Math.min(prim.r, prim.w / 2, prim.h / 2);
      ctx.beginPath();
      ctx.moveTo(prim.x + rad, prim.y);
      ctx.arcTo(prim.x + prim.w, prim.y, prim.x + prim.w, prim.y + prim.h, rad);
      ctx.arcTo(prim.x + prim.w, prim.y + prim.h, prim.x, prim.y + prim.h, rad);
      ctx.arcTo(prim.x, prim.y + prim.h, prim.x, prim.y, rad);
      ctx.arcTo(prim.x, prim.y, prim.x + prim.w, prim.y, rad);
      ctx.closePath();
      ctx.stroke();
    }
  }
  ctx.restore();
}

/** Activity state → glyph (UiStateIcon.icon). */
export function stateIconName(
  state: ItemState,
  status?: ItemStatus,
): CanvasIconName {
  switch (state) {
    case 'unplanned':
      return 'Ban';
    case 'planned':
      return 'Map';
    case 'done':
      return 'CheckCircle';
    case 'underway':
      return status === 'green'
        ? 'PlusSquare'
        : status === 'yellow'
          ? 'Triangle'
          : status === 'red'
            ? 'MinusSquare'
            : 'Target';
    default:
      return 'Ban';
  }
}

/**
 * Draw the state glyph centred at (x, y) the way the activity banner does (WI #5094):
 * no background disc, coloured like `stateIconColor`. Used by the phase ribbon
 * and the milestone state badge.
 */
export function drawStateIcon(
  ctx: CanvasRenderingContext2D,
  state: ItemState,
  status: ItemStatus | undefined,
  x: number,
  y: number,
  size: number,
): void {
  drawIcon(
    ctx,
    stateIconName(state, status),
    x,
    y,
    size,
    stateIconColor(state, status),
  );
}

/** Activity state → glyph colour (UiStateIcon `status-{tagType}` classes). */
export function stateIconColor(state: ItemState, status?: ItemStatus): string {
  if (state === 'underway') {
    if (status === 'green') {
      return cssVar('--color-success-700', '#085D3A');
    }
    if (status === 'yellow') {
      return cssVar('--color-warning-500', '#F79009');
    }
    if (status === 'red') {
      return cssVar('--color-error-500', '#F04438');
    }
  }

  return '#363f72'; // status-blue
}
